import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

interface TransactionStatus {
  transactionId: string;
  status: string;
  statusDescription: string;
  amount: number;
  currency: string;
  errorMessage?: string;
  createdAt: string;
  lastModifiedAt?: string;
}

export const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const transactionId =
    searchParams.get('transactionId') ||
    searchParams.get('order_id') ||
    searchParams.get('transaction_id');
  const bookingIdParam = searchParams.get('bookingId');

  const checkTransactionStatus = useCallback(async () => {
    if (!transactionId) {
      setLoading(false);
      if (!bookingIdParam) {
        setError('Transaction ID is missing');
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/payment/status/${transactionId}`);
      setTransactionStatus(response.data);

      if (response.data.status === 'Created' || response.data.status === 'Pending') {
        try {
          await axios.post(`/api/payment/refresh/${transactionId}`);
          const refreshedResponse = await axios.get(`/api/payment/status/${transactionId}`);
          setTransactionStatus(refreshedResponse.data);
        } catch (refreshErr) {
          console.warn('Could not refresh payment status:', refreshErr);
        }
      }
    } catch (err: unknown) {
      console.error('Error checking transaction status:', err);
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
          ? (err.response.data as { message?: string }).message
          : undefined;
      setError(msg || 'Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  }, [transactionId, bookingIdParam]);

  useEffect(() => {
    void checkTransactionStatus();
  }, [checkTransactionStatus]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stitch-canvas px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
          <p className="mt-4 text-lg text-stitch-muted">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  const bookingOnly = !transactionId && bookingIdParam && !error;

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stitch-canvas px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-pop">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-stitch-danger-soft">
              <span className="material-symbols-outlined text-3xl text-stitch-danger" style={{ fontVariationSettings: "'FILL' 1" }}>
                close
              </span>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-stitch-fg">Payment Verification Failed</h2>
            <p className="mt-2 text-stitch-muted">{error}</p>
            <div className="mt-6">
              <Link
                to="/bookings"
                className="inline-flex items-center justify-center rounded-stitch-lg bg-stitch-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-stitch-primary-hover"
              >
                Back to Bookings
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPaymentSuccessful = transactionStatus?.status === 'Success';

  if (bookingOnly) {
    return (
      <main className="flex min-h-screen flex-grow items-center justify-center bg-stitch-canvas px-4 py-12 sm:px-6 md:py-20 lg:px-8">
        <div className="w-full max-w-xl text-center">
          <div className="relative mb-6 inline-flex h-24 w-24 items-center justify-center rounded-full bg-stitch-success-soft">
            <span
              className="material-symbols-outlined relative text-5xl text-stitch-success"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              check_circle
            </span>
          </div>
          <h1 className="mb-4 text-3xl font-extrabold tracking-tight text-stitch-fg md:text-4xl">Thank you!</h1>
          <p className="text-lg text-stitch-muted">
            Your booking reference is <span className="font-semibold text-stitch-fg">#{bookingIdParam}</span>. You can
            view details in My Bookings once the payment provider confirms the transaction.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/bookings"
              className="inline-flex justify-center rounded-stitch-lg bg-stitch-primary px-6 py-3.5 text-sm font-semibold text-white hover:bg-stitch-primary-hover"
            >
              View My Bookings
            </Link>
            <Link
              to="/"
              className="inline-flex justify-center rounded-stitch-lg border border-stitch-border bg-stitch-surface px-6 py-3.5 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-grow items-center justify-center bg-stitch-canvas px-4 py-12 sm:px-6 md:py-20 lg:px-8">
      <div className="w-full max-w-xl">
        <div className="mb-10 text-center">
          <div className="relative mb-6 inline-flex h-24 w-24 items-center justify-center rounded-full bg-stitch-success-soft">
            {isPaymentSuccessful && (
              <div className="absolute inset-0 animate-ping rounded-full bg-stitch-success/20" />
            )}
            <span
              className="material-symbols-outlined relative text-5xl text-stitch-success"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              check_circle
            </span>
          </div>
          <h1 className="mb-4 text-3xl font-extrabold tracking-tight text-stitch-fg md:text-4xl">
            {isPaymentSuccessful ? 'Payment Successful!' : `Payment Status: ${transactionStatus?.status ?? 'Unknown'}`}
          </h1>
          <p className="text-lg leading-relaxed text-stitch-muted">
            {isPaymentSuccessful
              ? 'Thank you for booking with InterviewR. Your session is now confirmed and our expert has been notified.'
              : transactionStatus?.statusDescription}
          </p>
          {bookingIdParam && transactionId && (
            <p className="mt-2 text-sm text-stitch-muted">Reference: booking #{bookingIdParam}</p>
          )}
        </div>

        {transactionStatus && (
          <div className="mb-8 overflow-hidden rounded-stitch-xl border border-stitch-border bg-stitch-surface shadow-stitch-card">
            <div className="p-6 md:p-8">
              <h2 className="mb-6 text-xs font-semibold uppercase tracking-wider text-stitch-muted">
                Booking Details
              </h2>
              <dl className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-stitch-primary">receipt_long</span>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-tight text-stitch-muted">Transaction</dt>
                    <dd className="font-semibold text-stitch-fg">{transactionStatus.transactionId}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-stitch-accent">payments</span>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-tight text-stitch-muted">Amount</dt>
                    <dd className="font-semibold text-stitch-fg">
                      {transactionStatus.currency} {transactionStatus.amount.toFixed(2)}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3 md:col-span-2">
                  <span className="material-symbols-outlined text-stitch-primary">calendar_today</span>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-tight text-stitch-muted">Date</dt>
                    <dd className="font-semibold text-stitch-fg">
                      {new Date(transactionStatus.createdAt).toLocaleString()}
                    </dd>
                  </div>
                </div>
              </dl>
              {transactionStatus.errorMessage && (
                <p className="mt-4 text-sm text-stitch-danger">{transactionStatus.errorMessage}</p>
              )}
            </div>
          </div>
        )}

        {!transactionId && !transactionStatus && (
          <div className="mb-8 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 text-center text-stitch-muted">
            <p>No transaction id in URL. If you completed a payment, check your email or visit bookings.</p>
            {bookingIdParam && <p className="mt-2 text-sm">Booking reference: {bookingIdParam}</p>}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          {isPaymentSuccessful ? (
            <Link
              to="/bookings"
              className="inline-flex justify-center rounded-stitch-lg bg-stitch-success px-6 py-3.5 text-center text-sm font-semibold text-white shadow-stitch-card transition hover:opacity-90"
            >
              View My Bookings
            </Link>
          ) : transactionStatus?.status === 'Created' || transactionStatus?.status === 'Pending' ? (
            <button
              type="button"
              onClick={() => void checkTransactionStatus()}
              disabled={loading}
              className="inline-flex justify-center rounded-stitch-lg bg-stitch-info px-6 py-3.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh Payment Status'}
            </button>
          ) : (
            <Link
              to="/interviewers"
              className="inline-flex justify-center rounded-stitch-lg bg-stitch-primary px-6 py-3.5 text-sm font-semibold text-white hover:bg-stitch-primary-hover"
            >
              Try Again
            </Link>
          )}
          <Link
            to="/"
            className="inline-flex justify-center rounded-stitch-lg border border-stitch-border bg-stitch-surface px-6 py-3.5 text-sm font-medium text-stitch-fg-soft hover:bg-stitch-surface-2"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
};
